<script>
import { mapState } from 'vuex';
import flatPickr from 'vue-flatpickr-component';
import LeaveModel from '@models/Leave';
import BlockingContainer from '@components/BlockingContainer';
import datePickerUtility from '@mixins/datePickerUtility';
import isFunction from 'lodash/isFunction';
import editFormChange from '@mixins/editFormChange';
import EntityMultiselect from '@components/EntityMultiselect';
import DriverModel from '@models/Driver';

export default {
  components: {
    BlockingContainer,
    flatPickr,
    EntityMultiselect,
  },
  mixins: [datePickerUtility, editFormChange],
  props: {
    // eslint-disable-next-line
    loadedId: {
      default: null,
    },
  },
  data() {
    return {
      leave: {
        user_id: null,
        start_date: null,
        end_date: null,
        amount_of_days: null,
        description: null,
        confirmed: 0,
        driver: null,
      },
      defaultLeave: {},
      driverModel: DriverModel,
    };
  },
  computed: {
    ...mapState({
      dateConfig: (state) => state.common.dateConfig,
    }),
  },
  mounted() {
    this.defaultLeave = Object.assign({}, this.leave);
    if (this.loadedId !== 'new') {
      this.$wait.start('leave loading');
      LeaveModel.find(this.loadedId).then((response) => {
        this.leave = Object.assign({}, response.leave);
        this.$wait.end('leave loading');
        this.$nextTick(() => {
          this.initChangeWatcher(['leave'], this.validate);
        });
      });
    }
  },
  methods: {
    validate(callback) {
      this.$validator.validateAll('leave-form').then((result) => {
        if (result) {
          this.submit(callback);
        }
      });
    },
    submit(callback) {
      this.$wait.start('leave loading');
      const data = Object.assign({}, this.leave);
      // remove unnecessary data
      delete data.driver;
      delete data.confirmed_by;

      new LeaveModel(data)
        .save()
        .then((response) => {
          this.leave = Object.assign({}, this.defaultLeave);
          this.$validator.reset();
          this.$emit('submitted', response.leave);
          if (callback && isFunction(callback)) callback();
          this.$wait.end('leave loading');
        })
        .catch(() => this.$wait.end('leave loading'));
    },
  },
};
</script>
<template>
  <div>
    <BlockingContainer v-if="$wait.is('leave loading')">&nbsp;</BlockingContainer>
    <b-form data-vv-scope="leave-form" @submit.prevent="validate">
      <b-form-row>
        <b-col>
          <b-form-group :label="`${$t('fields.driver')}:`">
            <EntityMultiselect
              v-model="leave.user_id"
              v-validate="'required'"
              data-vv-name="user_id"
              :data-vv-as="$t('fields.driver')"
              :initial-value="leave.driver"
              :state="errors.has('user_id', 'leave-form')"
              :placeholder="$t('placeholders.driver')"
              sort="first_name"
              :model="driverModel"
            >
            </EntityMultiselect>

            <div v-show="errors.has('user_id', 'leave-form')" class="invalid-feedback d-block m-0">
              {{ errors.first('user_id', 'leave-form') }}
            </div>
          </b-form-group>
        </b-col>
      </b-form-row>
      <b-form-row>
        <b-col sm="4">
          <b-form-group>
            <template v-slot:label class="col-form-legend col-12">
              {{ $t('fields.start_date') }}:
              <span class="status-star text-danger">*</span>
            </template>
            <div :class="{ 'is-invalid': errors.has('start_date', 'leave-form') }">
              <flat-pickr
                v-model="leave.start_date"
                v-validate="'required'"
                data-vv-name="start_date"
                :data-vv-as="$t('fields.start_date')"
                aria-describedby="input-feedback-start_date"
                :config="dateConfig"
                class="form-control"
                :class="{ 'is-invalid': errors.has('start_date', 'leave-form') }"
                :placeholder="$t('placeholders.select_date')"
                @on-close="closeCalendar('leave.start_date', 'dd.MM.yyyy', 'yyyy-MM-dd', ...arguments); leave.end_date = leave.start_date"
              >
              </flat-pickr>
              <b-form-invalid-feedback id="input-feedback-start_date">
                <template v-show="errors.has('start_date', 'leave-form')">{{ errors.first('start_date', 'leave-form') }} </template>
              </b-form-invalid-feedback>
            </div>
          </b-form-group>
        </b-col>
        <b-col sm="4">
          <b-form-group>
            <template v-slot:label class="col-form-legend col-12">
              {{ $t('fields.end_date') }}:
              <span class="status-star text-danger">*</span>
            </template>
            <flat-pickr
              v-model="leave.end_date"
              v-validate="'required'"
              data-vv-name="end_date"
              :data-vv-as="$t('fields.end_date')"
              aria-describedby="input-feedback-end_date"
              :config="dateConfig"
              class="form-control"
              :class="{ 'is-invalid': errors.has('end_date', 'leave-form') }"
              :placeholder="$t('placeholders.select_date')"
              @on-close="closeCalendar('leave.end_date', 'dd.MM.yyyy', 'yyyy-MM-dd', ...arguments)"
            >
            </flat-pickr>
            <b-form-invalid-feedback id="input-feedback-end_date">
              <template v-show="errors.has('end_date', 'leave-form')">{{ errors.first('end_date', 'leave-form') }} </template>
            </b-form-invalid-feedback>
          </b-form-group>
        </b-col>
        <b-col sm="4">
          <b-form-group>
            <template v-slot:label class="col-form-legend col-12">
              {{ $t('fields.amount_of_days') }}
              <span class="status-star text-danger">*</span>
            </template>
            <b-form-select
              v-model="leave.amount_of_days"
              v-validate="'required'"
              data-vv-value-path="localValue"
              data-vv-name="amount_of_days"
              :data-vv-as="$t('fields.amount_of_days')"
              :state="errors.has('amount_of_days', 'leave-form') ? false : null"
              aria-describedby="input-feedback-amount_of_days"
            >
              <option :value="null">{{ $t('placeholders.select_amount') }}</option>
              <option :value="0.5">0,5</option>
              <option v-for="index in 30" :key="`amount-${index}`" :value="index">{{ index }}</option>
            </b-form-select>

            <b-form-invalid-feedback id="input-feedback-cost_type_id">
              <template v-show="errors.has('cost_type_id', 'expense-form')">{{ errors.first('cost_type_id', 'expense-form') }}</template>
            </b-form-invalid-feedback>
          </b-form-group>
        </b-col>
      </b-form-row>
      <b-form-row>
        <b-col sm="12">
          <b-form-group>
            <template v-slot:label class="col-form-legend col-12"> {{ $t('fields.description') }} (optional): </template>
            <b-form-textarea
              id="leaveDescriptionTextArea"
              v-model="leave.description"
              v-validate="'max:65000'"
              data-vv-value-path="localValue"
              data-vv-name="description"
              :data-vv-as="$t('fields.description')"
              :placeholder="$t('placeholders.description')"
              :rows="3"
              :state="errors.has('description', 'leave-form') ? false : null"
              aria-describedby="input-feedback-description"
            >
            </b-form-textarea>

            <b-form-invalid-feedback id="input-feedback-description">
              <template v-show="errors.has('description', 'leave-form')">
                {{ errors.first('description', 'expense-form') }}
              </template>
            </b-form-invalid-feedback>
          </b-form-group>
        </b-col>
      </b-form-row>
      <b-button ref="leaveSubmitBtn" type="submit" :disabled="errors.any('leave-form')" :variant="'outline-' + (!errors.any('leave-form') ? 'dark' : 'danger')">
        {{ $t('actions.submit') }}
      </b-button>
    </b-form>
  </div>
</template>
